import type { FC, ReactNode } from 'react'

import { useId } from 'react'
import { cn } from '@/lib/utils'

interface BrandMarkProps {
    className?: string
}

const BIG_SPARK = 'M10.4164 7.77843C10.7232 7.77843 10.972 8.02716 10.972 8.33398C10.972 11.4817 13.5186 14.0284 16.6664 14.0284C16.9732 14.0284 17.222 14.2772 17.222 14.584C17.222 14.8908 16.9732 15.1395 16.6664 15.1395C13.5186 15.1395 10.972 17.6862 10.972 20.834C10.972 21.1408 10.7232 21.3895 10.4164 21.3895C10.1096 21.3895 9.86084 21.1408 9.86084 20.834C9.86084 17.6862 7.31416 15.1395 4.1664 15.1395C3.85957 15.1395 3.61084 14.8908 3.61084 14.584C3.61084 14.2772 3.85957 14.0284 4.1664 14.0284C7.31416 14.0284 9.86084 11.4817 9.86084 8.33398C9.86084 8.02716 10.1096 7.77843 10.4164 7.77843Z'

const SMALL_SPARK = 'M18.6108 3.33398C18.9177 3.33398 19.1664 3.58272 19.1664 3.88954C19.1664 4.96455 20.0358 5.83398 21.1108 5.83398C21.4177 5.83398 21.6664 6.08272 21.6664 6.38954C21.6664 6.69636 21.4177 6.9451 21.1108 6.9451C20.0358 6.9451 19.1664 7.81453 19.1664 8.88954C19.1664 9.19637 18.9177 9.4451 18.6108 9.4451C18.304 9.4451 18.0553 9.19637 18.0553 8.88954C18.0553 7.81453 17.1859 6.9451 16.1108 6.9451C15.804 6.9451 15.5553 6.69636 15.5553 6.38954C15.5553 6.08272 15.804 5.83398 16.1108 5.83398C17.1859 5.83398 18.0553 4.96455 18.0553 3.88954C18.0553 3.58272 18.304 3.33398 18.6108 3.33398Z'

// The glowing app-icon mark from the original logo, drawn as SVG so it stays crisp at any size.
export const BrandMark: FC<BrandMarkProps> = ({ className }): ReactNode => {
    const id = useId().replace(/[^a-zA-Z0-9_-]/g, '')
    const glowId = `${id}-glow`
    const vignetteId = `${id}-vignette`
    const sparkId = `${id}-spark`

    return (
        <svg
            viewBox='0 0 25 25'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            aria-hidden='true'
            focusable='false'
            className={cn('size-6 shrink-0', className)}
        >
            <rect width='25' height='25' rx='7.5' fill={`url(#${glowId})`} />
            <rect width='25' height='25' rx='7.5' fill={`url(#${vignetteId})`} fillOpacity='0.3' />
            <rect
                x='0.418'
                y='0.418'
                width='24.164'
                height='24.164'
                rx='7.082'
                stroke='#7B8FDD'
                strokeOpacity='0.24'
                strokeWidth='0.836'
            />
            <path fillRule='evenodd' clipRule='evenodd' d={BIG_SPARK} fill={`url(#${sparkId})`} />
            <path fillRule='evenodd' clipRule='evenodd' d={SMALL_SPARK} fill={`url(#${sparkId})`} />

            <defs>
                <radialGradient
                    id={glowId}
                    cx='0'
                    cy='0'
                    r='1'
                    gradientUnits='userSpaceOnUse'
                    gradientTransform='translate(12.1556 -28.1944) rotate(90) scale(61.9754)'
                >
                    <stop offset='0.492' stopColor='#0D0123' stopOpacity='0' />
                    <stop offset='0.652' stopColor='#4409A1' />
                    <stop offset='0.786' stopColor='#BE3DD9' />
                    <stop offset='0.846' stopColor='#FDBEFA' />
                </radialGradient>

                <radialGradient
                    id={vignetteId}
                    cx='0'
                    cy='0'
                    r='1'
                    gradientUnits='userSpaceOnUse'
                    gradientTransform='translate(12.5 12.5) rotate(90) scale(12.5 17.9275)'
                >
                    <stop offset='0.4' stopColor='#393C67' stopOpacity='0' />
                    <stop offset='0.99' stopColor='#393C67' />
                </radialGradient>

                <linearGradient id={sparkId} x1='3.6' y1='12' x2='21.7' y2='12' gradientUnits='userSpaceOnUse'>
                    <stop stopColor='#FFFFFF' />
                    <stop offset='1' stopColor='#B5B5B5' />
                </linearGradient>
            </defs>
        </svg>
    )
}
